import { createSelector } from 'reselect';
import { selectRouteParams } from 'selectors/general';

import { roster, scheduleInsights, waivers } from 'store/saga/entities/bound';
import { selectLatestVenueWaiver } from 'features/VenuePage/VenuePage.selectors';
import { convertFromRaw } from 'draft-js';

const yieldManagementSelector = state => state.yieldManagement;

export const selectScheduleListFilter = createSelector(yieldManagementSelector, ym => ym.scheduleListFilter);
export const selectActiveScheduleId = createSelector(
  yieldManagementSelector,
  selectRouteParams,
  (ym, routeParams) => routeParams.scheduleId || ym.selectedScheduleId
);
export const selectLastActiveVenueId = createSelector(yieldManagementSelector, ym => ym.lastActiveVenueId);

export const selectScheduleInsightsIsLoaded = createSelector(
  state => state || {},
  selectActiveScheduleId,
  (state, scheduleId) => scheduleInsights.loaded(state, scheduleId)
);
export const selectScheduleInsightsData = createSelector(
  state => state || {},
  selectActiveScheduleId,
  (state, scheduleId) => scheduleInsights.data(state, scheduleId)
);
export const selectActiveScheduleInsights = createSelector(
  selectScheduleInsightsIsLoaded,
  selectScheduleInsightsData,
  (isLoaded, data) => (isLoaded ? data : {})
);
export const selectScheduleInsightsHasError = createSelector(
  state => state || {},
  selectActiveScheduleId,
  (state, scheduleId) => Boolean(scheduleInsights.error(state, scheduleId))
);

export const selectActiveScheduleDetailTab = createSelector(selectRouteParams, routeParams => routeParams.tab || null);

export const selectRosterIsLoaded = createSelector(
  state => state || {},
  selectActiveScheduleId,
  (state, scheduleId) => roster.loaded(state, scheduleId)
);
export const selectRosterData = createSelector(
  state => state || {},
  selectActiveScheduleId,
  (state, scheduleId) => roster.data(state, scheduleId)
);
export const selectActiveRoster = createSelector(
  (_, schedule) => schedule,
  selectRosterIsLoaded,
  selectRosterData,
  (schedule, isLoaded, data) => (isLoaded ? data.map(reservation => ({ ...reservation, schedule })) : [])
);

export const selectEnabledWaiverId = createSelector(selectLatestVenueWaiver, waiver =>
  waiver.data.waiver && waiver.data.isEnabled ? waiver.data.waiverId : null
);

export const selectEnabledWaiverVersionId = createSelector(selectLatestVenueWaiver, waiver =>
  waiver.data.waiver && waiver.data.isEnabled ? waiver.data.waiverVersionId : null
);

export const selectReservation = createSelector(
  selectRosterData,
  (state, reservationId) => reservationId,
  (reservations, reservationId) =>
    reservations
      ? reservations.find(({ reservationId: scheduleReservationId }) => scheduleReservationId === reservationId)
      : null
);

export const selectWaiverVersion = createSelector(
  (state, key) => waivers.select(state, key),
  ({ data, error, loading, loaded }) => {
    const { waiverVersion } = data || {};

    return {
      error,
      isLoading: loading,
      isLoaded: loaded,
      data: !loaded
        ? {}
        : {
            waiver: waiverVersion?.waiverData && convertFromRaw(JSON.parse(waiverVersion.waiverData)),
          },
    };
  }
);
